import { Fragment, useEffect, useState } from "react";
import { LayerContent } from "../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import useAnalytics from "../../../../../hooks/useAnalytics";
import moment from "moment";
import {
  doTransformData,
  fallIncidentPlaceCols2,
  handlePointClicked,
  transformDataForBarChart,
} from "../formUtils";
import CustomerLoader from "../../../../../components/CustomLoader";
import SectionModal, { DefaultContainer } from "./SectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { incidentLists } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";

const CaregiversReporting = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: any) => {
  const [tableData, setTableData] = useState<any>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [caregiver, setCaregiver] = useState("");
  const [caregiverId, setCaregiverId] = useState("");
  const [showCaregiverModal, setShowCaregiverModal] = useState(false);
  const [caregiverDetails, setCaregiverDetails] = useState<any>([]);

  const { startDate, endDate, fallFacility } = useAnalyticsWrapper((state) => ({
    startDate: state.startDate,
    endDate: state.endDate,
    fallFacility: state.fallFacility,
  }));

  const { getCaregiversReportingIncidents, getCaregiversReportingDetails } =
    useAnalytics({
      facility: fallFacility?.value || "",
      startDate: moment(startDate).format("yyyy-MM-DD"),
      endDate: moment(endDate).format("yyyy-MM-DD"),
      createdBy: caregiver,
      createdById: caregiverId,
    });

  const { data: caregiversData, isFetching: isFetchingCaregivers } =
    getCaregiversReportingIncidents;

  const { data: caregiversDetailsData, isFetching: isLoadingDetails } =
    getCaregiversReportingDetails;

  useEffect(() => {
    if (caregiversData !== undefined) {
      // Deep clone the data to prevent mutation of original data
      const tempData = JSON.parse(JSON.stringify(caregiversData?.data.data));

      if (!!tempData?.incidentCreatedBy) {
        // Filter out the single incident reports
        const singleIncidents = tempData.incidentCreatedBy.filter(
          (x: any) => x.count === 1
        );
        tempData.incidentCreatedBy = tempData.incidentCreatedBy.filter(
          (x: any) => x.count > 1
        );
        tempData.totalCount -= singleIncidents.length;
        tempData.incidentCreatedBy.forEach((item: any) => {
          item.percentage = ((item.count / tempData.totalCount) * 100).toFixed(
            2
          );
        });
      }

      const transformedTableData = doTransformData(
        tempData,
        "incidentCreatedBy",
        "residentName",
        "Total"
      );
      const transformedBarData = transformDataForBarChart(
        transformedTableData,
        "Caregiver Count"
      );
      setTableData(transformedTableData);
      shareTableData(transformedTableData);
      setBarData(transformedBarData);
    }
  }, [caregiversData]);

  useEffect(() => {
    if (caregiversDetailsData) {
      const tempDetails = caregiversDetailsData.data.data.map((item: any) => ({
        ...item,
      }));
      setCaregiverDetails(tempDetails);
    }
  }, [caregiversDetailsData]);

  const title = "Caregivers Reporting Multiple Incidents";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    // Match the drill-down by the caregiver's Contact Id (compound Name isn't
    // filterable with Middle Name on). Bars aggregate by name, so only trust the
    // Id when exactly one row matches the label; otherwise fall back to name.
    const matchedRows = (tableData ?? []).filter(
      (row: any) => row.residentName === label
    );
    const resolvedId =
      matchedRows.length === 1 ? matchedRows[0].residentId ?? "" : "";
    setShowCaregiverModal(true);
    setCaregiver(label);
    setCaregiverId(resolvedId);
  };

  useEffect(() => {
    let cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    let cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };
    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="residentName" title="Resident" />
          <GridColumn
            field="count"
            title="Number"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  return (
    <LayerContent>
      {isFetchingCaregivers ? (
        <CustomerLoader />
      ) : (
        <DefaultContainer
          title={title}
          grid={grid}
          handleBarClicked={(chart: any) => handleBarClicked(chart)}
          barData={barData}
          barType="horizontal"
        />
      )}
      {showCaregiverModal ? (
        <SectionModal
          open={showCaregiverModal}
          facilityName={fallFacility?.label}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowCaregiverModal(false)}
          resetFn={() => {
            setCaregiver("");
            setCaregiverId("");
            setCaregiverDetails(undefined);
          }}
          isLoading={isLoadingDetails || isFetchingSelectedIncident}
          tableData={caregiverDetails}
          title={`${title} - ${caregiver}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: caregiverDetails,
              columnData: incidentLists(),
              fileName: fallFacility?.label,
              startDate,
              endDate,
              subTitle: `${title} - ${caregiver}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              caregiverDetails,
              incidentLists(),
              `${fallFacility?.label} - ${title} - ${caregiver}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default CaregiversReporting;
